import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { opportunityApi } from '../services/opportunityApi';
import {
  TrendingUp, TrendingDown, Car, Sparkles, Shield, Zap, Clock, Lock,
  ArrowRight, Send, AlertCircle, ChevronRight, Search
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------
const Section = ({ children, className = '' }) => (
  <section className={`mb-16 ${className}`}>{children}</section>
);

const SectionTitle = ({ children, sub }) => (
  <div className="text-center mb-10">
    <h2 className="text-2xl md:text-3xl font-bold text-primary mb-3">{children}</h2>
    {sub && <p className="text-secondary max-w-xl mx-auto">{sub}</p>}
  </div>
);

// ---------------------------------------------------------------------------
// INSIGHT_CARDS — configurable array, easy to extend
// ---------------------------------------------------------------------------
const INSIGHT_CARDS = [
  {
    id: 'sales_profile',
    icon: TrendingUp,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/20',
    title: 'Profil de vente',
    compute: (data) => {
      if (!data.profile?.sold_count) return null;
      const makes = data.profile.makes?.slice(0, 3).join(', ') || 'N/A';
      const priceRange = data.profile.price_min && data.profile.price_max
        ? `${Math.round(data.profile.price_min / 1000)}k - ${Math.round(data.profile.price_max / 1000)}k`
        : null;
      return {
        value: `${data.profile.sold_count} vehicules vendus`,
        description: `Marques principales : ${makes}${priceRange ? ` | Segment : ${priceRange} EUR` : ''}`,
      };
    },
  },
  {
    id: 'stock_analysis',
    icon: Car,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
    title: 'Analyse du stock',
    compute: (data) => {
      if (!data.stock_count) return null;
      return {
        value: `${data.stock_count} vehicules en stock`,
        description: 'Votre inventaire est analyse en continu pour eviter le surstock sur les memes segments',
      };
    },
  },
  {
    id: 'opportunities_count',
    icon: Sparkles,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
    title: 'Opportunites detectees',
    compute: (data) => {
      if (!data.opportunities_available) return null;
      return {
        value: `${data.opportunities_available} opportunites`,
        description: 'Vehicules sous-cotes correspondant a votre profil en ce moment',
      };
    },
  },
];

// ---------------------------------------------------------------------------
// PILLARS — 3 value proposition cards
// ---------------------------------------------------------------------------
const PILLARS = [
  {
    icon: TrendingDown,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/20',
    title: 'Vehicules sous-cotes',
    desc: 'Detection automatique des annonces en dessous du prix marche. Nos algorithmes comparent chaque annonce a plus de 137 000 references.',
  },
  {
    icon: Shield,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
    title: 'Anti-surstock intelligent',
    desc: 'Analyse de votre stock actuel et de vos ventes passees pour ne proposer que des vehicules complementaires a votre inventaire.',
  },
  {
    icon: Zap,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
    title: 'Reactivite',
    desc: '30% des bonnes affaires partent dans les 3 premiers jours. On surveille pour vous — pas besoin de rester devant votre ecran.',
  },
];

// ---------------------------------------------------------------------------
// Blurred opportunity card placeholder
// ---------------------------------------------------------------------------
const BlurredCard = ({ index }) => {
  const items = [
    { make: 'BMW', model: 'Serie 3', price: '18 500', discount: '-15%' },
    { make: 'Audi', model: 'A4 Avant', price: '22 900', discount: '-12%' },
    { make: 'VW', model: 'Golf 8', price: '16 200', discount: '-18%' },
    { make: 'Mercedes', model: 'Classe A', price: '24 500', discount: '-11%' },
    { make: 'Peugeot', model: '3008', price: '19 800', discount: '-14%' },
    { make: 'Renault', model: 'Arkana', price: '21 300', discount: '-9%' },
  ];
  const item = items[index % items.length];

  return (
    <div className="rounded-xl border border-dark-border bg-dark-card overflow-hidden select-none">
      {/* Fake image */}
      <div className="h-36 bg-gradient-to-br from-dark-hover to-dark-surface relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <Car className="h-10 w-10 text-dark-border" />
        </div>
        {/* Discount badge */}
        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-emerald-500/20 border border-emerald-500/30">
          <span className="text-xs font-semibold text-emerald-400">{item.discount}</span>
        </div>
      </div>
      {/* Fake content */}
      <div className="p-3 space-y-2">
        <div className="text-sm font-semibold text-primary">{item.make} {item.model}</div>
        <div className="flex items-center gap-2 text-xs text-muted">
          <span>2023</span>
          <span>52 000 km</span>
          <span>Diesel</span>
        </div>
        <div className="text-lg font-bold text-carmin">{item.price} &euro;</div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// OpportunitiesConversion component
// ---------------------------------------------------------------------------
const OpportunitiesConversion = () => {
  const { user, refreshUser } = useAuth();

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const data = await opportunityApi.getPreview();
        setPreview(data);
      } catch {
        // Preview not available — that's fine
      } finally {
        setLoading(false);
      }
    };
    fetchPreview();
  }, []);

  const isPending = user?.opportunities_status === 'requested' || submitted;

  const handleRequestAccess = async () => {
    setSubmitting(true);
    try {
      await opportunityApi.requestAccess();
      setSubmitted(true);
      if (refreshUser) await refreshUser();
    } catch {
      // Silently handle
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto pb-16">
      {/* ================================================================ */}
      {/* PENDING BANNER                                                    */}
      {/* ================================================================ */}
      {isPending && (
        <div className="mb-8 rounded-xl border border-amber-500/30 bg-amber-500/5 p-5 flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
            <Clock className="h-5 w-5 text-amber-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-amber-300 mb-1">Demande en cours de verification</h3>
            <p className="text-sm text-secondary">
              Votre demande d'acces aux Opportunites a ete envoyee. Notre equipe l'examine dans les plus brefs delais.
            </p>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* HERO                                                              */}
      {/* ================================================================ */}
      <Section>
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-carmin/10 via-dark-card to-dark-surface border border-carmin/20 p-8 md:p-12">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-carmin/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />

          <div className="relative z-10 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-carmin/10 border border-carmin/20 mb-6">
              <Search className="h-3.5 w-3.5 text-carmin" />
              <span className="text-xs font-medium text-carmin">Opportunites Pro</span>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              Trouvez les meilleures affaires avant tout le monde
            </h1>

            <p className="text-lg text-secondary mb-8 leading-relaxed">
              Notre algorithme analyse le marche en continu pour detecter les vehicules sous-cotes
              qui correspondent a votre profil de vente.
            </p>

            {!isPending && (
              <button
                onClick={handleRequestAccess}
                disabled={submitting}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-carmin hover:bg-carmin-dark text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    Demander l'acces
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </Section>

      {/* ================================================================ */}
      {/* PERSONALIZED ANALYSIS                                             */}
      {/* ================================================================ */}
      <Section>
        <SectionTitle sub="Basee sur votre historique de ventes et votre stock actuel.">
          Votre analyse personnalisee
        </SectionTitle>

        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : preview && preview.has_sufficient_data ? (
          <div className="grid md:grid-cols-3 gap-5">
            {INSIGHT_CARDS.map((card) => {
              const result = card.compute(preview);
              if (!result) return null;
              const Icon = card.icon;
              return (
                <div
                  key={card.id}
                  className={`rounded-xl border ${card.border} ${card.bg} p-6 transition-all hover:scale-[1.02] hover:shadow-lg`}
                >
                  <div className={`w-10 h-10 rounded-lg ${card.bg} flex items-center justify-center mb-4`}>
                    <Icon className={`h-5 w-5 ${card.color}`} />
                  </div>
                  <h3 className="text-sm font-medium text-muted mb-1">{card.title}</h3>
                  <p className="text-xl font-bold text-primary mb-2">{result.value}</p>
                  <p className="text-sm text-secondary leading-relaxed">{result.description}</p>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="max-w-lg mx-auto">
            <div className="rounded-xl border border-dark-border bg-dark-card p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="h-6 w-6 text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-primary mb-2">Donnees insuffisantes</h3>
              <p className="text-sm text-secondary leading-relaxed">
                Donnees insuffisantes sur votre profil pour generer votre analyse personnalisee.
                Ajoutez des vehicules a votre stock et enregistrez vos ventes pour debloquer cette fonctionnalite.
              </p>
            </div>
          </div>
        )}
      </Section>

      {/* ================================================================ */}
      {/* 3 PILLARS                                                        */}
      {/* ================================================================ */}
      <Section>
        <SectionTitle sub="Un algorithme taille pour les professionnels de l'automobile.">
          Pourquoi les Opportunites Carmin ?
        </SectionTitle>

        <div className="grid md:grid-cols-3 gap-5">
          {PILLARS.map((p) => (
            <div
              key={p.title}
              className={`rounded-xl border ${p.border} ${p.bg} p-6 transition-all hover:scale-[1.02] hover:shadow-lg`}
            >
              <div className={`w-10 h-10 rounded-lg ${p.bg} flex items-center justify-center mb-4`}>
                <p.icon className={`h-5 w-5 ${p.color}`} />
              </div>
              <h3 className="text-lg font-semibold text-primary mb-2">{p.title}</h3>
              <p className="text-sm text-secondary leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ================================================================ */}
      {/* BLURRED PREVIEW                                                  */}
      {/* ================================================================ */}
      <Section>
        <SectionTitle sub="Un apercu de ce qui vous attend.">
          Des opportunites triees sur le volet
        </SectionTitle>

        <div className="relative">
          {/* Blurred cards grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 blur-[6px] select-none pointer-events-none">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <BlurredCard key={i} index={i} />
            ))}
          </div>

          {/* Overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-dark-bg/40">
            <div className="text-center bg-dark-card/95 backdrop-blur-sm rounded-2xl border border-carmin/30 p-8 shadow-2xl max-w-sm mx-4">
              <div className="w-14 h-14 rounded-full bg-carmin/10 flex items-center justify-center mx-auto mb-4">
                <Lock className="h-7 w-7 text-carmin" />
              </div>
              <h3 className="text-lg font-semibold text-primary mb-2">
                {preview?.opportunities_available > 0
                  ? `${preview.opportunities_available} opportunites disponibles`
                  : 'Opportunites reservees aux abonnes'}
              </h3>
              <p className="text-sm text-secondary mb-5">
                Debloquez l'acces pour decouvrir les meilleures affaires du marche.
              </p>
              {!isPending && (
                <button
                  onClick={handleRequestAccess}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-carmin hover:bg-carmin-dark text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Demander l'acces
                  <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </Section>

      {/* ================================================================ */}
      {/* CTA SECTION                                                      */}
      {/* ================================================================ */}
      {!isPending && (
        <Section>
          <div id="request-section" className="scroll-mt-8">
            <SectionTitle sub="Activation par notre equipe sous 24h.">
              Pret a saisir les opportunites ?
            </SectionTitle>

            <div className="flex flex-col items-center">
              <button
                onClick={handleRequestAccess}
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl bg-carmin hover:bg-carmin-dark text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Demander l'acces
                  </>
                )}
              </button>
              <p className="text-xs text-muted mt-4">
                Activation par notre equipe sous 24h.
              </p>
            </div>
          </div>
        </Section>
      )}
    </div>
  );
};

export default OpportunitiesConversion;
